import { useEffect, useState } from "react";
import { MessageCircle, Phone } from "lucide-react";
import { SITE, WHATSAPP_HREF } from "@/lib/site";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { ctaClick } from "@/lib/ctaTracking";
import { getVisitorSessionId } from "@/lib/ctaTracking";
import {
  getAssignedVariant,
  recordExposure,
  type WhatsAppVariant,
} from "@/lib/abTest";

// Persistent floating action stack: WhatsApp + Call, visible on every page.
//
// The WhatsApp button runs a 3-way A/B test (icon, text, placement). The
// variant is assigned after hydration so server-rendered markup stays stable;
// clicks are tagged with the variant for the report at /admin/ab-whatsapp.

export function FloatingContact() {
  const [variant, setVariant] = useState<WhatsAppVariant | null>(null);

  useEffect(() => {
    const assigned = getAssignedVariant();
    if (!assigned) return;
    setVariant(assigned);
    recordExposure(assigned, getVisitorSessionId());
  }, []);

  const baseBtn =
    "inline-flex h-12 w-12 items-center justify-center rounded-full shadow-lg ring-1 ring-black/5 transition-transform duration-200 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2";

  const onLeft = variant === "left";

  const whatsappClasses =
    variant === "labeled"
      ? "inline-flex h-12 items-center gap-2 rounded-full px-4 shadow-lg ring-1 ring-black/5 text-sm font-medium transition-transform duration-200 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 bg-[#25D366] text-white hover:bg-[#1ebe57]"
      : `${baseBtn} bg-[#25D366] text-white hover:bg-[#1ebe57]`;

  return (
    <div
      className={`fixed bottom-5 z-40 flex flex-col gap-3 print:hidden ${
        onLeft ? "left-5 items-start" : "right-5 items-end"
      }`}
      aria-label="Quick contact"
    >
      <a
        href={WHATSAPP_HREF}
        target="_blank"
        rel="noopener noreferrer"
        onClick={ctaClick("whatsapp", "floating_button")}
        aria-label="WhatsApp Me"
        title="WhatsApp Me"
        className={whatsappClasses}
        data-ab-variant={variant ?? undefined}
      >
        {variant === "left" ? (
          <MessageCircle className="h-5 w-5" />
        ) : (
          <WhatsAppIcon />
        )}
        {variant === "labeled" && <span>WhatsApp Me</span>}
      </a>

      <a
        href={`tel:${SITE.phoneE164}`}
        onClick={ctaClick("phone", "floating_button")}
        aria-label={`Call ${SITE.phone}`}
        title={`Call ${SITE.phone}`}
        className={`${baseBtn} bg-primary text-primary-foreground hover:opacity-90`}
      >
        <Phone className="h-5 w-5" />
      </a>
    </div>
  );
}
